import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { RootState, AppDispatch } from '../store';
import { getHourlyWeather } from '../store/weatherSlice';
import { Line } from 'react-chartjs-2';
import { Chart, LineElement, PointElement, LinearScale, CategoryScale, Tooltip, Legend } from 'chart.js';

Chart.register(LineElement, PointElement, LinearScale, CategoryScale, Tooltip, Legend);

const CityDetailsPage: React.FC = () => {
  const { cityId } = useParams<{ cityId: string }>();
  const dispatch: AppDispatch = useDispatch();
  const navigate = useNavigate();
  const city = useSelector((state: RootState) => state.cityList.find(c => c.id === cityId));
  const weather = useSelector((state: RootState) => state.weather[cityId || '']);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (city && (!weather?.hourly || !weather.hourly.length)) {
      setLoading(true);
      fetch(`https://api.openweathermap.org/geo/1.0/direct?q=${encodeURIComponent(city.name)}&limit=1&appid=${process.env.REACT_APP_OWM_API_KEY}`)
        .then(res => res.json())
        .then(data => {
          if (data[0]) {
            dispatch(getHourlyWeather({ cityId: city.id, lat: data[0].lat, lon: data[0].lon }))
              .finally(() => setLoading(false));
          } else {
            setLoading(false);
          }
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cityId, city, dispatch]);

  if (!city) {
    return <div>City not found <button onClick={() => navigate('/')}>Back</button></div>;
  }

  if (!weather?.weather) {
    return <div>Loading...</div>;
  }

  const hourly = weather.hourly?.slice(0, 8) || [];
  if (!hourly.length) {
    return <div>Loading hourly data...</div>;
  }

  const chartData = {
    labels: hourly.map(h => {
      const date = new Date(h.dt * 1000);
      return `${date.getHours()}:00`;
    }),
    datasets: [
      {
        label: 'Temperature (°C)',
        data: hourly.map(h => h.temp),
        fill: false,
        borderColor: '#5409DA',
        backgroundColor: '#4E71FF',
        tension: 0.3,
      },
    ],
  };

  return (
    <div style={{ maxWidth: 900, margin: '0 auto', padding: 24 }}>
      <button
        onClick={() => navigate('/')}
        style={{
          marginBottom: 24,
          background: 'linear-gradient(90deg, #5409DA 0%, #4E71FF 100%)',
          color: '#fff',
          fontWeight: 600,
          border: 'none',
          borderRadius: 8,
          padding: '8px 20px',
          cursor: 'pointer',
          boxShadow: '0 2px 8px rgba(78,113,255,0.15)',
          fontSize: 16,
          transition: 'background 0.2s',
        }}
        onMouseOver={e => (e.currentTarget.style.background = 'linear-gradient(90deg, #4E71FF 0%, #5409DA 100%)')}
        onMouseOut={e => (e.currentTarget.style.background = 'linear-gradient(90deg, #5409DA 0%, #4E71FF 100%)')}
      >
        ← Back
      </button>
      <h2 style={{fontSize: "2rem",display: "block", margin: "20px 0px" }}>{city.name}</h2>
      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          gap: 32,
          alignItems: 'center',
          flexWrap: 'wrap',
        }}
      >
        <div style={{ flex: '1 1 260px', minWidth: 220, maxWidth: 340 }}>
          <div style={{ marginBottom: 24 }}>
            <strong>Current:</strong> {weather.weather.temp}°C, {weather.weather.description}
            <br />
            Humidity: {weather.weather.humidity}%
            <br />
            Wind: {weather.weather.wind} m/s
            <br />
            <img src={`https://openweathermap.org/img/wn/${weather.weather.icon}@2x.png`} alt={weather.weather.description} />
          </div>
        </div>
        <div style={{ flex: '2 1 340px', minWidth: 260, maxWidth: 520 }}>
          {loading ? (
            <div>Loading chart...</div>
          ) : (
            <Line data={chartData} />
          )}
        </div>
      </div>
    </div>
  );
};

export default CityDetailsPage; 